/* eslint-disable react/jsx-props-no-spreading */
import { Box, Button, ButtonGroup, Divider, Flex, Grid, GridItem, Stack } from '@chakra-ui/react';
import { ExtensionType, IntegrationSourceType } from '@/client/services/api/graphql/gql/graphql';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import ArchiveExpertModal from '../overlay/ArchiveExpertModal';
import { CircleCaret } from '@/client/components/icons/ContinuIcons';
import ConfirmationModal from '../../../shared/overlay/confirmation-modals/ConfirmationModal';
import type { ExpertFormData } from '../forms/types';
import ExpertGeneralDetailsForm from '../forms/ExpertGeneralDetailsForm';
import ExpertIntegrationsForm from '../forms/integrations/ExpertIntegrationsForm';
import ExpertPreview from '@/client/components/admin/ai-agent/experts/forms/ExpertPreview';
import ExpertSegmentationForm from '../forms/ExpertSegmentationForm';
import FormNavigationBlocker from '@/client/components/admin/shared/overlay/FormNavigationBlocker';
import Loading from '@/client/components/media/Loading';
import SolidButton from '@/client/components/shared/buttons/SolidButton';
import { generateRandomHexColor } from '@/client/utils/colors/generateRandomHexColor';
import { useCheckFeatureFlag } from '@/client/services/hooks/auth/useCheckFeatureFlag';
import { useCreateExpert } from '../hooks/useCreateExpert';
import { useEffect } from 'react';
import { useExpertsStore } from '@/client/components/admin/ai-agent/experts/expertsStore';
import { useGetOneExpert } from '../hooks/useGetOneExpert';
import { useGetPublicImage } from '@/client/services/hooks/media/useGetPublicImage';
import { useListExperts } from '../hooks/useListExperts';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { useUpdateExpert } from '../hooks/useUpdateExpert';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {useAuthStore} from '@/client/services/state/authStore';
import {last} from 'lodash';

const expertFormSchema = z.object({
  name: z.string().min(1, { message: 'Required field' }),
  enabled: z.boolean(),
  color: z
    .string()
    .min(1, { message: 'Color is required' })
    .refine((color) => /^#([0-9a-fA-F]{6})$/.test(color), { message: 'Invalid color' }),
  description: z.string().min(1, { message: 'Required field' }),
  resources: z
    .array(
      z.object({
        addedBy: z.string(),
        extensionName: z.string(),
        extensionType: z.nativeEnum(ExtensionType),
        id: z.string().optional(),
        path: z.string(),
        source: z.nativeEnum(IntegrationSourceType),
        sourceId: z.string().nullish(),
        downloadLink: z.string().nullish(),
        isFolder: z.boolean().optional(),
        fileCount: z.number().optional().nullish(),
        parentFolder: z.string().nullish(),
        lastSynced: z.string().nullish(),
        lastSyncSuccessful: z.boolean().nullish(),
        lastSyncErrorMessage: z.string().nullish(),
      }),
    )
    .min(1, { message: 'Please upload at least one file' }),
  segmentation: z.array(
    z.object({
      __typename: z.string(),
      segmentationId: z.string(),
      segmentationType: z.string(),
      segmentationName: z.string(),
      parent: z
        .object({
          segmentationId: z.string(),
          segmentationName: z.string(),
          segmentationType: z.string(),
          parent: z
            .object({
              segmentationId: z.string(),
              segmentationName: z.string(),
              segmentationType: z.string(),
            })
            .nullish(),
        })
        .nullish(),
    }),
  ),
});

export default function ExpertDetails() {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { getPublicImage } = useGetPublicImage();
  const { authConfig } = useAuthStore();
  const { company, user } = authConfig;
  const navigate = useNavigate();
  const { expertId } = useParams();
  const {
    isEditingExpert,
    setIsEditingExpert,
    setSelectedExpertId,
    setArchiveModalIsOpen,
    updatingExpertModalIsOpen,
    setUpdatingExpertModalIsOpen,
  } = useExpertsStore();
  const expertsEnabled = useCheckFeatureFlag('enable_ai_eddy_experts');

  if (!expertsEnabled) {
    return <Navigate to="/admin/eddy/experts" replace />;
  }

  useEffect(() => () => setIsEditingExpert(false), [setIsEditingExpert]);

  const { data, isLoading, isError } = useGetOneExpert(expertId);
  const {
    data: expertLimitData,
    isLoading: expertLimitIsLoading,
    isError: expertLimitIsError,
  } = useListExperts({ includeTotalCount: true, pageSize: 1 });

  useEffect(() => {
    if (expertLimitData && !expertId) {
      const { limitInfo } = expertLimitData;
      const { totalCurrentCount, limit } = limitInfo;
      const limitReached = totalCurrentCount >= limit;

      if (limitReached) {
        setToast({
          show: true,
          status: 'error',
          title: t('admin.aiAgent.experts.details.limitReachedError'),
        });

        navigate('/admin/eddy/experts');
      }
    }
  }, [expertLimitData, expertId, setToast]);

  const methods = useForm<ExpertFormData>({
    defaultValues: {
      id: '',
      name: '',
      enabled: false,
      color: generateRandomHexColor(),
      description: '',
      segmentation: [],
      resources: [],
      googleDriveIntegration: user.google_drive_integrated,
      confluenceIntegration: false,
      localUploadIntegration: true,
    },
    resolver: zodResolver(expertFormSchema),
    mode: 'onChange',
  });

  const {
    reset,
    handleSubmit,
    trigger,
    formState: { isValid, isDirty, errors },
  } = methods;

  useEffect(() => {
    if (errors) {
      console.log('errors', errors);
    }
  }, [errors]);

  useEffect(() => {
    if (data) {
      reset({
        id: data.id,
        name: data.name,
        enabled: data.enabled,
        color: data.color,
        description: data.description,
        resources: data.resources.map((resource: any) => ({
          addedBy: resource.addedBy,
          extensionName: resource.extensionName,
          extensionType: resource.extensionType,
          id: resource.id,
          path: resource.path,
          source: resource.source,
          sourceId: resource.sourceId,
          downloadLink: resource.downloadLink,
          isFolder: resource.isFolder || false,
          fileCount: resource.fileCount,
          parentFolder: resource.parentFolder,
          lastSynced: resource.lastSynced,
          lastSyncSuccessful: resource.lastSyncSuccessful,
          lastSyncErrorMessage: resource.lastSyncErrorMessage,
        })),
        segmentation: data.segmentation.map((seg: any) => ({
          __typename: seg.__typename,
          segmentationId: seg.segmentationId,
          segmentationType: seg.segmentationType,
          segmentationName: seg.segmentationName,
          parent: seg.parent
            ? {
                segmentationId: seg.parent.segmentationId,
                segmentationName: seg.parent.segmentationName,
                segmentationType: seg.parent.segmentationType,
                parent: seg.parent.parent
                  ? {
                      segmentationId: seg.parent.parent.segmentationId,
                      segmentationName: seg.parent.parent.segmentationName,
                      segmentationType: seg.parent.parent.segmentationType,
                    }
                  : undefined,
              }
            : undefined,
        })),
        googleDriveIntegration: user.google_drive_integrated,
        confluenceIntegration: false,
        localUploadIntegration: false,
      });

      return;
    }

    reset();
  }, [data]);

  const { createExpert } = useCreateExpert();
  const { updateExpert } = useUpdateExpert();

  const onSubmit: SubmitHandler<ExpertFormData> = async (formData) => {
    if (expertId) {
      await updateExpert.mutateAsync({
        expertId,
        formData,
      });
    } else {
      await createExpert.mutateAsync({ formData });
    }

    setIsEditingExpert(false);
  };

  if ((!!expertId && isLoading) || expertLimitIsLoading) return <Loading />;

  if ((!!expertId && isError) || expertLimitIsError)
    return <div>{t('admin.aiAgent.experts.details.error')}</div>;

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
          <Grid templateColumns="repeat(12, 1fr)" height="100%" overflow="hidden">
            <GridItem position="relative" colSpan={9} height="100%" overflow="hidden">
              <Flex direction="column" height="100%" overflow="hidden">
                <Flex
                  backgroundColor="white"
                  zIndex={999}
                  justifyContent="flex-start"
                  alignItems="center"
                  paddingY={6}
                  flexShrink={0}
                  paddingX={8}
                >
                  <Button
                    variant="ghost"
                    leftIcon={<CircleCaret />}
                    paddingLeft={0}
                    onClick={() => navigate('/admin/eddy/experts')}
                  >
                    {t('admin.aiAgent.experts.details.backToExperts')}
                  </Button>
                </Flex>

                <Box overflowY="auto" flex={1} minHeight={0} paddingBottom={8} paddingX={8}>
                  <ExpertGeneralDetailsForm />

                  <Stack spacing={12}>
                    <Divider borderColor="neutral.100" />

                    <ExpertSegmentationForm />

                    <Divider borderColor="neutral.100" />

                    <ExpertIntegrationsForm />
                  </Stack>
                </Box>

                {isEditingExpert && (
                  <Flex
                    zIndex={888}
                    background="white"
                    borderTop="1px solid"
                    borderTopColor="warmNeutral.200"
                    paddingY={4}
                    paddingX={18}
                    justifyContent="space-between"
                    alignItems="center"
                    flexShrink={0}
                  >
                    <Box>
                      {expertId && !data?.enabled && (
                        <Button
                          variant="redGhost"
                          onClick={() => {
                            setSelectedExpertId(expertId);
                            setArchiveModalIsOpen(true);
                          }}
                        >
                          {t('admin.aiAgent.experts.details.deleteExpert')}
                        </Button>
                      )}
                    </Box>

                    <ButtonGroup>
                      <Button
                        variant="cancel"
                        onClick={() => {
                          reset();
                          setIsEditingExpert(false);
                        }}
                      >
                        {t('global.actions.cancel')}
                      </Button>

                      <SolidButton
                        type="submit"
                        isDisabled={!isValid || !isDirty}
                        onMouseOver={() => trigger()}
                      >
                        {t('admin.aiAgent.experts.details.saveChanges')}
                      </SolidButton>
                    </ButtonGroup>
                  </Flex>
                )}
              </Flex>
            </GridItem>

            <GridItem
              colSpan={3}
              backgroundColor="neutral.0"
              borderLeft="1px solid"
              borderColor="neutral.100"
              padding={6}
              paddingBottom={4}
            >
              <ExpertPreview />
            </GridItem>
          </Grid>
        </form>
      </FormProvider>

      <ArchiveExpertModal />

      <ConfirmationModal
        isOpen={updatingExpertModalIsOpen}
        onClose={() => setUpdatingExpertModalIsOpen(false)}
        imageSrc={getPublicImage('continu/app-images/waiting-patiently.svg')}
        title={t('expertUpdatingModal.title')}
        descriptionOne={t('expertUpdatingModal.descriptionOne')}
        submitButtonOptions={{
          label: t('expertUpdatingModal.submitButtonLabel'),
          onClick: () => {
            navigate('/admin/eddy/experts');
            setUpdatingExpertModalIsOpen(false);
          },
        }}
      />

      <FormNavigationBlocker enabled={isDirty && isEditingExpert} />
    </>
  );
}
